import { ApolloError, gql, useApolloClient, useQuery } from '@apollo/client'
import {
    useAuthorizationContext,
    useIsLoggedIn,
} from '@emico-hooks/login-token'
import { useCallback, useEffect } from 'react'

import { getSentry } from '@emico/utils'

import { Product } from './ProductPage/GetProduct.query'
import { ConfigurableProduct } from '../graphql/schema.generated'
import { RecentlyViewedItems } from '../typePolicies'

const getRecentlyViewedProducts = gql`
    query GetRecentlyViewedProducts {
        customer {
            recentlyViewed {
                products {
                    id
                    sku
                }
            }
        }
    }
`
const getRecentlyViewedProductsGuest = gql`
    query GetRecentlyViewedProducts {
        recentlyViewed @client {
            id
        }
    }
`

const trackProdViewMutation = gql`
    mutation TrackProductView($sku: String!) {
        trackProductView(sku: $sku) {
            result
        }
    }
`

export type LastViewedProductsType = Array<
    Pick<ConfigurableProduct | Product, 'id' | 'sku'>
>

type LastViewed = {
    customer: {
        recentlyViewed: {
            products: RecentlyViewedProduct[]
        }
    }
}

export type RecentlyViewedProduct = {
    id: number
    __typename?: string
}

type RecentlyViewedProductGuest = {
    recentlyViewed: RecentlyViewedProduct[]
}

export const useLastViewedProducts = () => {
    const context = useAuthorizationContext()
    const isLoggedIn = useIsLoggedIn()

    const { data, loading, error } = useQuery<LastViewed>(
        getRecentlyViewedProducts,
        {
            skip: !isLoggedIn,
            context,
        },
    )

    const { data: dataGuest, error: errorLoadingGuestData } =
        useQuery<RecentlyViewedProductGuest>(getRecentlyViewedProductsGuest)

    const err = error || errorLoadingGuestData

    const errMessage = err
        ? err instanceof ApolloError
            ? err.graphQLErrors?.[0]?.message
            : (err as any)?.message
        : undefined

    const uniqueProducts = Array.from(
        new Set<number>([
            ...(data?.customer?.recentlyViewed?.products?.map(
                (prod) => prod.id,
            ) ?? []),
            ...(dataGuest?.recentlyViewed?.map((prod) => prod.id) ?? []),
        ]),
    )

    return {
        products: [
            ...(uniqueProducts ?? []).map((prod) => ({ id: prod })),
        ].slice(0, RecentlyViewedItems.MAX_ITEMS),
        loading,
        error: errMessage,
    }
}

export const useTrackProductView = () => {
    const client = useApolloClient()
    const context = useAuthorizationContext()
    const isLoggedIn = useIsLoggedIn()

    const trackProduct = useCallback(
        async (id: number, productSku: string) => {
            if (!productSku || !id) {
                return
            }
            try {
                if (isLoggedIn) {
                    await client.mutate<{ result: boolean }>({
                        mutation: trackProdViewMutation,
                        context,
                        variables: { sku: productSku },
                        refetchQueries: [
                            { query: getRecentlyViewedProducts, context },
                        ],
                    })
                } else {
                    // For guests, update local cache & localStorage
                    client.cache.updateQuery(
                        { query: getRecentlyViewedProductsGuest },
                        (data) => {
                            try {
                                const currentItems: RecentlyViewedProduct[] =
                                    data?.recentlyViewed || []

                                const newSkuItem: RecentlyViewedProduct = {
                                    id,
                                    __typename: RecentlyViewedItems.TYPENAME,
                                }

                                const updatedItems = [
                                    newSkuItem,
                                    ...currentItems
                                        .filter((item) => item.id !== id)
                                        .slice(
                                            0,
                                            RecentlyViewedItems.MAX_ITEMS,
                                        ),
                                ]
                                localStorage.setItem(
                                    RecentlyViewedItems.STORAGE_KEY,
                                    JSON.stringify(updatedItems),
                                )
                                return {
                                    recentlyViewed: updatedItems,
                                }
                            } catch (err) {
                                return {
                                    recentlyViewed: data?.recentlyViewed ?? [],
                                }
                            }
                        },
                    )
                }
            } catch (err) {
                // No need to inform user, just hand it over to Sentry
                getSentry()?.captureException(err)
            }
        },
        [client, context, isLoggedIn],
    )

    return trackProduct
}
